import React, { useState } from 'react';
import { Search, Filter, X, RotateCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ReportFilters } from '@/services/reportService';

interface ReportFilterBarProps {
  filters: ReportFilters;
  onFilterChange: (newFilters: ReportFilters) => void;
  availableBatches: string[];
  availableFarms: Array<{ id: string; name: string }>;
  showStatusFilter?: boolean;
}

export const ReportFilterBar: React.FC<ReportFilterBarProps> = ({
  filters,
  onFilterChange,
  availableBatches,
  availableFarms,
  showStatusFilter = true,
}) => {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const activeFiltersCount = [
    filters.batchCode && filters.batchCode !== 'all',
    filters.farmId && filters.farmId !== 'all',
    filters.status && filters.status !== 'all',
    Boolean(filters.search),
  ].filter(Boolean).length;

  const handleReset = () => {
    onFilterChange({
      batchCode: 'all',
      farmId: 'all',
      status: 'all',
      search: '',
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm space-y-3">
      {/* Desktop Filter Row */}
      <div className="hidden md:flex items-center gap-3 flex-wrap">
        {/* Search Bar */}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={filters.search || ''}
            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
            placeholder="Search cycle, raiser, or farm..."
            className="pl-9 h-9 text-xs border-slate-300"
          />
        </div>

        {/* Batch Filter */}
        <div className="min-w-[130px]">
          <select
            value={filters.batchCode || 'all'}
            onChange={(e) => onFilterChange({ ...filters, batchCode: e.target.value })}
            aria-label="Filter by Batch"
            className="w-full h-9 px-2.5 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-600 text-slate-700 font-medium"
          >
            <option value="all">All Batches</option>
            {availableBatches.map((b) => (
              <option key={b} value={b}>
                Batch {b}
              </option>
            ))}
          </select>
        </div>

        {/* Farm Location Filter */}
        <div className="min-w-[150px]">
          <select
            value={filters.farmId || 'all'}
            onChange={(e) => onFilterChange({ ...filters, farmId: e.target.value })}
            aria-label="Filter by Farm Location"
            className="w-full h-9 px-2.5 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-600 text-slate-700 font-medium"
          >
            <option value="all">All Farm Locations</option>
            {availableFarms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        {showStatusFilter && (
          <div className="min-w-[120px]">
            <select
              value={filters.status || 'all'}
              onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}
              aria-label="Filter by Cycle Status"
              className="w-full h-9 px-2.5 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-600 text-slate-700 font-medium"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="settled">Settled</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        )}

        {/* Reset Filters */}
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="h-9 px-2 text-xs text-slate-500 hover:text-slate-800"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Reset
          </Button>
        )}
      </div>

      {/* Mobile Search & Filter Button */}
      <div className="md:hidden flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={filters.search || ''}
            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
            placeholder="Search cycle, raiser..."
            className="pl-9 h-9 text-xs border-slate-300"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsMobileFilterOpen(true)}
          className="h-9 px-3 border-slate-300 text-xs font-semibold flex items-center gap-1"
        >
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <span>Filters</span>
          {activeFiltersCount > 0 && (
            <span className="ml-1 px-1.5 py-0.2 bg-emerald-700 text-white rounded-full text-[10px]">
              {activeFiltersCount}
            </span>
          )}
        </Button>
      </div>

      {/* Mobile Filter Slide-over / Sheet */}
      {isMobileFilterOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end md:hidden">
          <div className="bg-white w-[85%] max-w-sm h-full p-5 shadow-2xl flex flex-col justify-between overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                <div className="flex items-center gap-2 font-bold text-slate-900 text-sm">
                  <Filter className="w-4 h-4 text-emerald-700" />
                  <span>Report Filters</span>
                </div>
                <button
                  onClick={() => setIsMobileFilterOpen(false)}
                  className="p-1 rounded-md text-slate-400 hover:text-slate-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Batch Code</label>
                <select
                  value={filters.batchCode || 'all'}
                  onChange={(e) => onFilterChange({ ...filters, batchCode: e.target.value })}
                  className="w-full h-11 min-h-[44px] px-3 text-xs bg-slate-50 border border-slate-300 rounded-md"
                >
                  <option value="all">All Batches</option>
                  {availableBatches.map((b) => (
                    <option key={b} value={b}>Batch {b}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Farm Location</label>
                <select
                  value={filters.farmId || 'all'}
                  onChange={(e) => onFilterChange({ ...filters, farmId: e.target.value })}
                  className="w-full h-11 min-h-[44px] px-3 text-xs bg-slate-50 border border-slate-300 rounded-md"
                >
                  <option value="all">All Farm Locations</option>
                  {availableFarms.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>

              {showStatusFilter && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Cycle Status</label>
                  <select
                    value={filters.status || 'all'}
                    onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}
                    className="w-full h-11 min-h-[44px] px-3 text-xs bg-slate-50 border border-slate-300 rounded-md"
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="settled">Settled</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-slate-200 flex gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex-1 h-11 min-h-[44px] text-xs font-semibold"
              >
                Reset
              </Button>
              <Button
                onClick={() => setIsMobileFilterOpen(false)}
                className="flex-1 h-11 min-h-[44px] bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
